import React, { useRef, useState } from 'react';
import { capitalize } from 'lodash-es';
import toast from 'react-hot-toast';
import { Info } from 'sushiweb/Icons';
import { Button } from '@grofers/react';
import Spinner from 'sushiweb/Spinner/Spinner';
import { grey } from 'sushiweb/tokens/color';

import history from '../../../../history';
import getStore from '../../../../store/store';
import Multiselect from 'multiselect-react-dropdown';
import { Star } from '../../../../UiKit/Star/Star';
import './WidgetCompCss.scss';
import { useMutationCall } from '../../../../api/react-client';
import { useClient } from '../../../../api/api-client';
import { EmptyState } from '../../../commons';
import { InvalidSearchImg } from '../../../../images/images';
import { getStringFromSlug } from '../../../../utils/UtilFunctions';

const switchRoleInfo = (currentRole, temporaryRoles = {}) => {
  return (
    <div className='tw-w-full tw-p-3 tw-bg-yellow-200 tw-rounded-lg tw-flex tw-flex-col tw-gap-1 tw-mt-2'>
      <div className='tw-text-200 tw-font-medium tw-text-grey-800'>
        <span className='tw-font-semibold tw-text-300'>
          Actual assigned primary role :
        </span>
        <span className='tw-ml-1 tw-text-300'>
          {capitalize(currentRole.replace(/_/g, ' '))}
        </span>
      </div>
      {temporaryRoles.primary_role ? (
        <div className='tw-text-200 tw-font-medium tw-text-grey-800'>
          <span className='tw-font-semibold tw-text-300'>
            Temporarily assigned primary role :
          </span>
          <span className='tw-ml-1 tw-text-300'>
            {getStringFromSlug(temporaryRoles?.primary_role)}
          </span>
        </div>
      ) : null}
      {temporaryRoles.add_on_roles?.length ? (
        <div className='tw-text-200 tw-font-medium tw-text-grey-800'>
          <span className='tw-font-semibold tw-text-300'>
            Temporarily assigned add-on roles :
          </span>
          <div className='tw-flex tw-flex-col tw-gap-1 tw-mt-1'>
            {temporaryRoles.add_on_roles.map((role, ind) => (
              <span className='tw-ml-6 tw-text-300' key={`${role}_${ind}`}>
                {`${ind + 1}. `}
                {getStringFromSlug(role)}
              </span>
            ))}
          </div>
        </div>
      ) : null}
      <div className='tw-text-200 tw-font-medium tw-text-grey-800 tw-flex tw-gap-1 tw-justify-start tw-mt-2'>
        <Info size={12} />
        <span className='tw-text-300'>
          The role switch would be temporary with expiry duration of 2 hours.
        </span>
      </div>
    </div>
  );
};

const SwitchRoleModal = ({ setShowPopUp }) => {
  const multiselectRef = useRef();
  const store = getStore(history);
  const state = store.getState();
  const { authState = {} } = state;
  const roleDetails = authState?.roleDetails;
  const [rolesSelected, setRolesSelected] = useState();
  const client = useClient();

  if (roleDetails?.isError)
    return (
      <EmptyState
        img={InvalidSearchImg}
        heading={roleDetails?.message || 'Something went wrong'}
      />
    );

  const {
    mutate: updateRole,
    isLoading: updatingRole,
    data,
  } = useMutationCall(
    {},
    `permissions/role_switch/`,
    ['update_role'],
    { doNotShowSuccessToast: true },
    (data) => {
      client(`permissions/role_switch/`, { data })
        .then((res) => {
          if (res.success) {
            setShowPopUp(false);
            window.location.reload();
            return toast.success('success');
          } else return toast.error(res.message || 'Something went wrong');
        })
        .catch((err) => {
          console.log('err', err);
          return toast.error('Something went wrong');
        });
    },
  );

  const handlePrimaryRoleSelected = (selectedList, selectedItem) => {
    setRolesSelected((prevState) => {
      return {
        ...prevState,
        base_role: selectedItem.id,
      };
    });
  };
  const handleAddOnRoleSelected = (selectedList, selectedItem) => {
    setRolesSelected((prevState) => {
      const add_on_roles = prevState?.add_on_roles?.length
        ? [...prevState?.add_on_roles, selectedItem.id]
        : [selectedItem.id];
      return {
        ...prevState,
        add_on_roles: add_on_roles,
      };
    });
  };
  const removeRole = (selectedList, removedItem) => {
    setRolesSelected((prevState) => {
      const addOnRoles = prevState?.add_on_roles;
      const ind = addOnRoles.indexOf(removedItem.id);
      addOnRoles.splice(ind, 1);
      return {
        ...prevState,
        add_on_roles: addOnRoles,
      };
    });
  };

  const submitHandle = () => {
    updateRole({
      base_role: rolesSelected.base_role,
      add_on_roles: rolesSelected.add_on_roles || [],
    });
  };

  return (
    <div className='tw-flex tw-flex-col tw-w-full tw-justify-between tw-items-end tw-h-[43vh] tw-gap-5'>
      <div className='tw-w-full tw-flex tw-flex-col tw-gap-3'>
        {roleDetails?.current_role ? (
          <>
            {switchRoleInfo(
              roleDetails?.current_role,
              roleDetails?.temporary_roles,
            )}
          </>
        ) : null}
        <div className='tw-flex tw-w-full tw-gap-4 tw-flex-col tw-mt-3'>
          <label
            htmlFor='primaryRoleInput'
            className='tw-w-full tw-flex tw-flex-col tw-relative'
            id='primary_roles_list'
            css={`
              .icon_down_dir {
                position: absolute !important;
              }
            `}
          >
            <div className='tw-flex'>
              <span className='tw-font-medium tw-text-400'>Primary Roles</span>
              <Star icon='*' />
            </div>
            <Multiselect
              ref={multiselectRef}
              closeOnSelect={true}
              avoidHighlightFirstOption
              selectionLimit={1}
              loading={false}
              placeholder='Select roles...'
              options={roleDetails?.base_roles}
              onSelect={handlePrimaryRoleSelected}
              displayValue='name'
            />
          </label>
          <label
            htmlFor='addOnRoleInput'
            className='tw-w-full tw-flex tw-flex-col '
            id='add_on_roles_list'
            css={`
              .rdw-list-dropdown,
              .rdw-text-align-dropdown {
                z-index: 0 !important;
              }
            `}
          >
            <span className='tw-font-medium tw-text-400 tw-mb-2 '>
              Add on Roles
            </span>
            <Multiselect
              ref={multiselectRef}
              avoidHighlightFirstOption
              singleSelect={false}
              loading={false}
              placeholder='Search roles...'
              options={roleDetails?.add_on_roles}
              onSelect={handleAddOnRoleSelected}
              onRemove={removeRole}
              displayValue='name'
            />
          </label>
        </div>
      </div>
      <Button
        variant={'fill'}
        className='tw-h-14 tw-z-50 tw-w-28'
        onClick={submitHandle}
        disabled={!rolesSelected?.base_role}
      >
        {updatingRole ? (
          <Spinner size='small' color={`${grey.z300}`} />
        ) : (
          'Submit'
        )}
      </Button>
    </div>
  );
};
const SwitchRole = ({ closePopUp, setShowPopUp, setPopDetails }) => {
  const store = getStore(history);
  const state = store.getState();
  const { authState = {} } = state;
  const roleDetails = authState?.roleDetails;
  const handleSwitchRolePopUp = () => {
    setShowPopUp(true);
    const switchRoleDetails = {
      type: 'medium',
      title: 'Switch Role',
      handlClose: closePopUp,
      minHeight: '40vh',
      maxHeight: '60vh',
      showScrollBar: false,
      component: <SwitchRoleModal setShowPopUp={setShowPopUp} />,
    };
    setPopDetails(switchRoleDetails);
  };
  const tempPimaryRole = roleDetails?.temporary_roles?.primary_role;
  return (
    <div
      className='tw-py-1 tw-w-full tw-border-solid tw-border-grey-200 tw-border-[1px] tw-border-x-0 tw-border-t-0 tw-pb-3 tw-font-medium tw-text-grey-700 tw-cursor-pointer tw-flex tw-justify-between tw-flex-col'
      onClick={handleSwitchRolePopUp}
    >
      <span> Switch Role</span>
      {tempPimaryRole ? (
        <span className='tw-w-full tw-text-200  '>
          <span className='tw-text-200 tw-text-semibold tw-text-grey-900'>
            Active Role:
          </span>{' '}
          {getStringFromSlug(tempPimaryRole)}
        </span>
      ) : null}
    </div>
  );
};

export default SwitchRole;
